/**
 * Typed API for jargon suggestions — the per-word review queue produced by the
 * `jargon-detection` pipeline step and transcript-line corrections.
 *
 * Mounts under /api/companies/:cid/jargon. All functions accept the
 * company-scoped Eden Treaty client returned by useCompanyApi().
 */

import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import type { IEnvelopeResult } from "./errors.ts"
import { unwrapData } from "./errors.ts"

export type TJargonStatus = "pending" | "accepted" | "dismissed"
export type TJargonSource = "detection" | "transcript_edit"
/** Review-queue order: newest first (default) or highest estimated frequency first. */
export type TJargonSort = "newest" | "frequency"
/** A decision applied to one or more suggestions at once. */
export type TBulkJargonAction = "accept" | "dismiss" | "restore"

export interface IJargonSuggestion {
  id: string
  meetingId: string
  term: string
  context: string | null
  frequency: number
  source: string
  status: string
  createdAt: string | null
  decidedAt: string | null
  decidedByEmail: string | null
  meetingSubject: string | null
}

/** One page of the review queue plus the unpaginated total (for count + "load more"). */
export interface IJargonSuggestionPage {
  suggestions: IJargonSuggestion[]
  total: number
}

export async function fetchJargonSuggestions(
  company: CompanyApi,
  opts?: { status?: TJargonStatus; meetingId?: string },
): Promise<IJargonSuggestion[]> {
  const query: Record<string, string> = {}
  if (opts?.status) query.status = opts.status
  if (opts?.meetingId) query.meetingId = opts.meetingId
  const result = (await company.jargon.suggestions.get({ query })) as IEnvelopeResult<{
    suggestions: IJargonSuggestion[]
    total: number
  }>
  return unwrapData(result).suggestions
}

/**
 * Paginated variant for the Jargon Manager page: returns the page of suggestions
 * plus the total matching the same filters, so the page can show a count and a
 * "load more" control. The per-meeting card ({@link fetchJargonSuggestions})
 * stays array-shaped and unaffected.
 */
export async function fetchJargonSuggestionsPage(
  company: CompanyApi,
  opts: { status: TJargonStatus; limit: number; offset?: number; sort?: TJargonSort },
): Promise<IJargonSuggestionPage> {
  const query: Record<string, string> = {
    status: opts.status,
    limit: String(opts.limit),
  }
  if (opts.offset !== undefined) query.offset = String(opts.offset)
  if (opts.sort) query.sort = opts.sort
  const result = (await company.jargon.suggestions.get({ query })) as IEnvelopeResult<IJargonSuggestionPage>
  return unwrapData(result)
}

/**
 * Apply one decision to a selection of suggestions in a single request. Accept
 * promotes the terms into the dictionary; dismiss silences them; restore sends
 * decided rows back to pending. Returns the distinct terms the action touched.
 */
export async function bulkDecideJargon(
  company: CompanyApi,
  ids: string[],
  action: TBulkJargonAction,
): Promise<{ action: TBulkJargonAction; terms: string[] }> {
  const result = (await company.jargon.suggestions.decide.post({ ids, action })) as IEnvelopeResult<{
    success: boolean
    action: TBulkJargonAction
    terms: string[]
  }>
  const { action: decided, terms } = unwrapData(result)
  return { action: decided, terms }
}

export async function acceptJargonSuggestion(
  company: CompanyApi,
  id: string,
  editedTerm?: string,
): Promise<{ term: string; status: string }> {
  const body = editedTerm?.trim() ? { term: editedTerm.trim() } : {}
  const result = (await company.jargon.suggestions({ id }).accept.post(body)) as IEnvelopeResult<{
    success: boolean
    term: string
    status: string
  }>
  return unwrapData(result)
}

export async function dismissJargonSuggestion(
  company: CompanyApi,
  id: string,
): Promise<{ term: string; status: string }> {
  const result = (await company.jargon.suggestions({ id }).dismiss.post()) as IEnvelopeResult<{
    success: boolean
    term: string
    status: string
  }>
  return unwrapData(result)
}
